import { useEffect, useMemo, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { Check, Crosshair, Dumbbell, ImagePlus, Link2, MapPin, Sparkles, X } from "lucide-react"
import toast from "react-hot-toast"
import { updateStore, uploadVehicleCoverImageToS3 } from "../../api/products/post"
import { detectCurrentPropertyLocation } from "../../utils/propertyLocation"

type Props = {
  isOpen: boolean
  store: any
  onClose: () => void
  onSaved: (store: any) => void
}

export default function GoalFitGymSetupModal({
  isOpen,
  store,
  onClose,
  onSaved,
}: Props) {
  const storeId = String(store?._id || store?.id || "")

  const [enabled, setEnabled] = useState(false)
  const [location, setLocation] = useState("")
  const [latitude, setLatitude] = useState("")
  const [longitude, setLongitude] = useState("")
  const [mapShareUrl, setMapShareUrl] = useState("")
  const [description, setDescription] = useState("")
  const [amenities, setAmenities] = useState("")
  const [coverImageUrl, setCoverImageUrl] = useState("")
  const [coverImageKey, setCoverImageKey] = useState("")
  const [coverFile, setCoverFile] = useState<File | null>(null)
  const [coverPreview, setCoverPreview] = useState("")
  const [saving, setSaving] = useState(false)
  const [geoLoading, setGeoLoading] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    if (!isOpen || !store) return

    const goalFitProfile = store?.goalfitProfile || {}

    const resolvedAmenities = Array.isArray(goalFitProfile?.amenities)
      ? goalFitProfile.amenities.join(", ")
      : Array.isArray(store?.amenities)
        ? store.amenities.join(", ")
        : String(goalFitProfile?.amenities || store?.amenities || "")

    const resolvedCoverImageUrl = String(
      goalFitProfile?.imageUrl ||
      goalFitProfile?.coverImageUrl ||
      store?.goalfitImageUrl ||
      store?.imageUrl ||
      store?.goalfitCoverImageUrl ||
      store?.coverImageUrl ||
      ""
    )

    setEnabled(Boolean(goalFitProfile?.enabled ?? store?.goalfitEnabled))
    setLocation(String(goalFitProfile?.location || store?.location || store?.address || ""))
    setLatitude(String(goalFitProfile?.latitude || store?.latitude || ""))
    setLongitude(String(goalFitProfile?.longitude || store?.longitude || ""))
    setMapShareUrl("")
    setDescription(String(goalFitProfile?.description || store?.description || ""))
    setAmenities(resolvedAmenities)
    setCoverImageUrl(resolvedCoverImageUrl)
    setCoverImageKey(String(goalFitProfile?.coverImageKey || store?.goalfitCoverImageKey || store?.coverImageKey || ""))
    setCoverFile(null)
    setCoverPreview(resolvedCoverImageUrl)
    setSaving(false)
    setGeoLoading(false)
    setError("")
  }, [isOpen, store])

  useEffect(() => {
    if (!coverFile) return

    const nextPreview = URL.createObjectURL(coverFile)
    setCoverPreview(nextPreview)

    return () => {
      URL.revokeObjectURL(nextPreview)
    }
  }, [coverFile])

  const amenityList = useMemo(
    () => amenities.split(",").map((item) => item.trim()).filter(Boolean),
    [amenities]
  )

  const handleDetectLocation = async () => {
    try {
      setGeoLoading(true)
      setError("")
      const detected = await detectCurrentPropertyLocation()
      setLatitude(String(detected.latitude || ""))
      setLongitude(String(detected.longitude || ""))
      if (detected.locationLabel && !location.trim()) {
        setLocation(detected.locationLabel)
      }
      toast.success("Position détectée avec succès")
    } catch (e: any) {
      setError(e?.message || "Impossible de détecter la position actuelle")
    } finally {
      setGeoLoading(false)
    }
  }

  const handleApplyMapShareUrl = () => {
    const rawValue = mapShareUrl.trim()

    if (!rawValue) {
      setError("Collez un lien Google Maps ou une localisation partagée")
      return
    }

    const normalized = decodeURIComponent(rawValue)
    const latLngPatterns = [
      /@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/,
      /[?&]q=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/,
      /[?&]query=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/,
      /!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/,
    ]

    for (const pattern of latLngPatterns) {
      const match = normalized.match(pattern)
      if (!match) continue

      setLatitude(match[1])
      setLongitude(match[2])
      setError("")
      toast.success("Coordonnées extraites depuis le lien partagé")
      return
    }

    setError("Impossible d'extraire les coordonnées depuis ce lien")
  }

  const handleSubmit = async () => {
    if (!storeId) return

    if (enabled) {
      if (!location.trim()) {
        setError("Ajoutez la localisation visible du gym")
        return
      }
      if (!description.trim()) {
        setError("Ajoutez une description pour GoalFit")
        return
      }
      if (!latitude.trim() || !longitude.trim()) {
        setError("Ajoutez les coordonnées du gym via géolocalisation ou lien de carte")
        return
      }
      if (!amenityList.length) {
        setError("Ajoutez au moins un amenity pour GoalFit")
        return
      }
      if (!coverPreview && !coverImageUrl && !coverFile) {
        setError("Ajoutez une image de couverture pour GoalFit")
        return
      }
    }

    try {
      setSaving(true)
      setError("")

      let nextCoverImageUrl = coverImageUrl
      let nextCoverImageKey = coverImageKey

      if (coverFile) {
        const uploaded = await uploadVehicleCoverImageToS3(coverFile, storeId)
        nextCoverImageUrl = uploaded?.imageUrl || uploaded?.coverImageUrl || nextCoverImageUrl
        nextCoverImageKey = uploaded?.coverImageKey || nextCoverImageKey
      }

      const payload = {
        goalfitEnabled: enabled,
        goalfitImageUrl: nextCoverImageUrl,
        goalfitCoverImageUrl: nextCoverImageUrl,
        goalfitCoverImageKey: nextCoverImageKey,
        imageUrl: nextCoverImageUrl,
        coverImageUrl: nextCoverImageUrl,
        coverImageKey: nextCoverImageKey,
        location: location.trim(),
        latitude: Number(latitude),
        longitude: Number(longitude),
        description: description.trim(),
        amenities: amenityList,
        goalfitProfile: {
          enabled,
          imageUrl: nextCoverImageUrl,
          coverImageUrl: nextCoverImageUrl,
          coverImageKey: nextCoverImageKey,
          location: location.trim(),
          latitude: Number(latitude),
          longitude: Number(longitude),
          description: description.trim(),
          amenities: amenityList,
        },
      }

      const updatedStore = await updateStore(storeId, payload)
      toast.success(enabled ? "Gym publie sur GoalFit" : "Configuration GoalFit mise a jour")
      onSaved(updatedStore)
      onClose()
    } catch (e: any) {
      setError(e?.message || "Impossible d'enregistrer la configuration GoalFit")
    } finally {
      setSaving(false)
    }
  }

  return (
    <AnimatePresence>
      {isOpen && store ? (
        <motion.div
          className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/60 px-3 py-4 backdrop-blur-sm sm:px-4 sm:py-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="mx-auto my-auto w-full max-w-4xl overflow-hidden rounded-3xl bg-white shadow-2xl"
            initial={{ scale: 0.96, y: 24 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.96, y: 24 }}
          >
            <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 via-white to-emerald-50 px-5 py-5 sm:px-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                    <Dumbbell size={20} />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-800">Go live on GoalFit</h2>
                    <p className="mt-1 text-sm text-slate-500">
                      Preparez {store?.name || "ce gym"} pour Nearby Gyms dans GoalFit.
                    </p>
                  </div>
                </div>

                <button onClick={onClose} className="rounded-lg p-1 hover:bg-slate-100">
                  <X />
                </button>
              </div>
            </div>

            <div className="grid gap-6 px-5 py-5 sm:px-6 lg:grid-cols-[minmax(0,1.15fr)_320px]">
              <div className="space-y-5">
                {error ? <div className="rounded-2xl bg-red-50 p-3 text-sm text-red-600">{error}</div> : null}

                <button
                  type="button"
                  onClick={() => setEnabled((prev) => !prev)}
                  className={`flex w-full items-center justify-between rounded-2xl border px-4 py-4 text-left transition ${
                    enabled
                      ? "border-emerald-300 bg-emerald-50"
                      : "border-slate-200 bg-slate-50 hover:bg-slate-100"
                  }`}
                >
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Publier ce gym sur GoalFit</p>
                    <p className="mt-1 text-xs text-slate-500">
                      Active la fiche GoalFit avec image, description, localisation et amenities.
                    </p>
                  </div>

                  <span
                    className={`flex h-6 w-6 items-center justify-center rounded-md border ${
                      enabled
                        ? "border-emerald-500 bg-emerald-500 text-white"
                        : "border-slate-300 bg-white text-transparent"
                    }`}
                  >
                    <Check size={15} />
                  </span>
                </button>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label className="text-sm font-medium text-slate-700">Image de couverture GoalFit</label>
                    <label className="mt-1.5 flex cursor-pointer items-center justify-between gap-3 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-600 transition hover:border-emerald-300 hover:bg-emerald-50">
                      <div className="flex items-center gap-3">
                        <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white text-emerald-600 shadow-sm">
                          <ImagePlus size={18} />
                        </span>
                        <div>
                          <p className="font-medium text-slate-800">Choisir une couverture</p>
                          <p className="mt-0.5 text-xs text-slate-500">Format paysage recommande pour la banniere GoalFit.</p>
                        </div>
                      </div>
                      <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200">
                        {coverFile ? coverFile.name : "Parcourir"}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(event) => setCoverFile(event.target.files?.[0] || null)}
                      />
                    </label>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700">Localisation visible</label>
                    <input
                      value={location}
                      onChange={(event) => setLocation(event.target.value)}
                      placeholder="Ex: Cocody Riviera, Abidjan"
                      className="mt-1.5 w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-800 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700">Coordonnées GPS</label>
                    <div className="mt-1.5 grid grid-cols-2 gap-3">
                      <input
                        value={latitude}
                        onChange={(event) => setLatitude(event.target.value)}
                        placeholder="Latitude"
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-800 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                      />
                      <input
                        value={longitude}
                        onChange={(event) => setLongitude(event.target.value)}
                        placeholder="Longitude"
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-800 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2 rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">Géocode du gym</p>
                        <p className="mt-1 text-xs text-slate-500">
                          Utilisez votre position actuelle ou collez un lien Google Maps partagé.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleDetectLocation}
                        disabled={geoLoading}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:opacity-60"
                      >
                        <Crosshair size={16} />
                        {geoLoading ? "Détection..." : "Utiliser ma position"}
                      </button>
                    </div>

                    <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                      <div className="flex-1">
                        <input
                          value={mapShareUrl}
                          onChange={(event) => setMapShareUrl(event.target.value)}
                          placeholder="Collez ici un lien Google Maps partagé"
                          className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-800 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleApplyMapShareUrl}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl border border-emerald-200 bg-white px-4 py-3 text-sm font-medium text-emerald-700 transition hover:bg-emerald-100"
                      >
                        <Link2 size={16} />
                        Extraire les coordonnées
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700">Amenities</label>
                    <input
                      value={amenities}
                      onChange={(event) => setAmenities(event.target.value)}
                      placeholder="Ex: Strength, Cardio, Coach, Showers"
                      className="mt-1.5 w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-800 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="text-sm font-medium text-slate-700">Description GoalFit</label>
                    <textarea
                      value={description}
                      onChange={(event) => setDescription(event.target.value)}
                      rows={4}
                      placeholder="Resumez l'ambiance, les equipements et le type d'accompagnement disponible."
                      className="mt-1.5 w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-800 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-950 text-white shadow-sm">
                  <div className="h-44 w-full bg-slate-900">
                    {coverPreview ? (
                      <img src={coverPreview} alt="GoalFit cover preview" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900">
                        <div className="rounded-2xl bg-white/10 p-4 text-white/70">
                          <ImagePlus size={28} />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-4 p-5">
                    <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-100">
                      <Sparkles size={12} /> GoalFit Nearby Gyms
                    </div>

                    <div>
                      <p className="text-lg font-semibold text-white">{store?.name || "Votre gym"}</p>
                      <p className="mt-2 flex items-center gap-2 text-sm text-white/70">
                        <MapPin size={14} />
                        {location.trim() || store?.city || "Localisation a renseigner"}
                      </p>
                      <p className="mt-1 text-xs text-white/55">
                        {latitude && longitude ? `${latitude}, ${longitude}` : "Coordonnées GPS à renseigner"}
                      </p>
                    </div>

                    <p className="text-sm leading-6 text-white/80">
                      {description.trim() || "Ajoutez une description claire pour expliquer le style du gym, les equipements et l'experience proposee."}
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {(amenityList.length ? amenityList : ["Strength", "Cardio", "Coach"]).slice(0, 4).map((amenity) => (
                        <span key={amenity} className="rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/80">
                          {amenity}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-slate-100 px-5 py-4 sm:px-6">
              <button
                onClick={onClose}
                className="rounded-xl border border-slate-200 px-4 py-2 text-slate-700 hover:bg-slate-50"
              >
                Annuler
              </button>
              <button
                disabled={saving}
                onClick={handleSubmit}
                className="rounded-xl bg-emerald-600 px-4 py-2 text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? "Enregistrement..." : "Enregistrer GoalFit"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
}
